import React, { useEffect, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Spinner,
  CardHeader,
} from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { slugify } from 'utils/slugify';
import { useCreateMutation, useUpdateMutation, useDetailQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { CreateShopFormValues, ShopCategory } from 'components/Types/Tenant/shop.type';
import FormikInput from '../Inputs/FormikInput';
import { handleFormErrors } from 'utils/formErrorHandler';

const formFields: {
  id: keyof CreateShopFormValues;
  label: string;
  required?: boolean;
  disabled?: boolean;
}[] = [
  { id: 'name', label: 'Shop Name', required: true },
  { id: 'slug', label: 'Slug', required: true, disabled: true },
  { id: 'country', label: 'Country' },
  { id: 'state', label: 'State' },
  { id: 'city', label: 'City' },
  { id: 'streetAddress', label: 'Street Address' },
  { id: 'contactNumber', label: 'Contact Number' },
];

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Shop Name is required'),
  slug: Yup.string()
    .trim()
    .required('Slug is required')
    .matches(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
    .min(3, 'Slug must be at least 3 characters')
    .max(63, 'Slug must be at most 63 characters'),
  city: Yup.string().trim().required('City is required'),
  state: Yup.string().trim().required('State is required'),
  country: Yup.string().trim().required('Country is required'),
  streetAddress: Yup.string().trim().required('Street Address is required'),
  contactNumber: Yup.string().trim().required('Contact Number is required'),
});

const CreateOrUpdateShopForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const shopCategoryOptions = Object.values(ShopCategory);

  const { data: fetchedShop, isLoading: loadingShop } = useDetailQuery<CreateShopFormValues>(
    API_ENDPOINTS.TENANT.SHOPS.WITH_ID,
    {
      id: id ?? '',
    },
  );

  const { mutate: createShop, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.TENANT.SHOPS.BASE,
  );
  const { mutate: updateShop, isPending: updating } = useUpdateMutation(
    API_ENDPOINTS.TENANT.SHOPS.BASE,
  );

  const initialValues: CreateShopFormValues = {
    name: '',
    slug: '',
    city: '',
    state: '',
    country: '',
    streetAddress: '',
    contactNumber: '',
    shopCategory: undefined,
    ...fetchedShop,
  };

  const formik = useFormik<CreateShopFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<CreateShopFormValues>) => {
      const payload = {
        ...values,
        name: values.name.trim(),
        slug: values.slug.trim(),
        city: values.city?.trim() || undefined,
        state: values.state?.trim() || undefined,
        country: values.country?.trim() || undefined,
        streetAddress: values.streetAddress?.trim() || undefined,
        contactNumber: values.contactNumber?.trim() || undefined,
        shopCategory: values.shopCategory,
      };

      const onSuccess = () => {
        navigate('/shops');
      };

      const onError = (error: any) => {
        handleFormErrors<CreateShopFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        // updateShop({ id, ...payload }, { onSuccess, onError });
      } else {
        createShop(payload, { onSuccess, onError });
      }
    },
  });

  useEffect(() => {
    if (!isEditMode && formik.values.name) {
      const generatedSlug = slugify(formik.values.name);
      formik.setFieldValue('slug', generatedSlug);
    }
  }, [formik.values.name, isEditMode]);

  const handleSlugChange = (val: string) => {
    const cleaned = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
    formik.setFieldValue('slug', cleaned);
  };

  if (isEditMode && loadingShop) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md={12} lg={8}>
        <Card className="p-4 shadow-sm">
          <CardHeader>
            <h2 className="text-center fw-bold fs-2">
              {isEditMode ? 'Update Shop' : 'Create a New Shop'}
            </h2>
          </CardHeader>

          <CardBody>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Row>
                {formFields.map((field) => (
                  <Col key={field.id} sm={12} md={6} lg={6}>
                    <FormikInput
                      field={field}
                      formik={formik}
                      onChangeCustom={field.id === 'slug' ? handleSlugChange : undefined}
                    />
                  </Col>
                ))}

                <Col sm={12} md={6} lg={6}>
                  <FormGroup>
                    <Label for="shopCategory">Shop Category</Label>
                    <select
                      id="shopCategory"
                      name="shopCategory"
                      className={`form-control ${
                        formik.touched.shopCategory && formik.errors.shopCategory
                          ? 'is-invalid'
                          : ''
                      }`}
                      value={formik.values.shopCategory || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isEditMode}
                    >
                      <option value="">Select Category</option>
                      {shopCategoryOptions.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    {formik.touched.shopCategory && formik.errors.shopCategory && (
                      <div className="invalid-feedback">{formik.errors.shopCategory}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex justify-content-center mt-4">
                <Button color="primary" type="submit" disabled={creating || updating}>
                  {creating || updating
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Shop'
                    : 'Create Shop'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(CreateOrUpdateShopForm);
